import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import {
  AppDialogRef,
  AppDialogService,
} from '@nimmsta/ng-commons-lib/services/app-dialog';
import { CustomValidators } from '@nimmsta/ng-commons-lib/utils/custom-validators';
import {
  DataConnectionEndpoint,
  DataConnectionStatus,
  EndpointMethod,
} from '@nimmsta/ng-commons-lib/models';
import { NimmstaCommonsModuleConfiguration } from '@nimmsta/ng-commons-lib/config';
import { safeEvalWithParams } from '@nimmsta/ng-commons-lib/utils/eval';
import { InputWithErrorComponent } from '@nimmsta/ng-commons-lib/forms';
import { EndpointForm, HttpForm } from './httpForm';
import { HttpDataConnection } from './httpDataConnection';
import { HttpAuthentication, HttpConfiguration } from './httpConfiguration';
import { KeyValueFormComponent } from './keyValueForm.component';
import { DataConnectionService } from '../../../services/dataConnection.service';
import { CodeComponent } from './code.component';
import { NgClass, NgIf } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

_('dataconnection.authentication');
_('dataconnection.jobListEndpoint');
_('dataconnection.jobListEndpoint_none');
_('dataconnection.username');
_('dataconnection.password');
_('dataconnection.token');
_('dataconnection.endpoint.identifier');
_('dataconnection.endpoint.method');
_('dataconnection.endpoint.url');
_('dataconnection.endpoint.isLocal');

_('error.noSpaceOrSpecial');

@Component({
  selector: 'app-http-form',
  templateUrl: 'http-form.component.html',
  imports: [
    ReactiveFormsModule,
    NgClass,
    TranslatePipe,
    NgIf,
    KeyValueFormComponent,
    TranslatePipe,
    InputWithErrorComponent,
  ],
})
export class HttpFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private service = inject(DataConnectionService);
  private modalService = inject(AppDialogService);
  private configuration = inject(NimmstaCommonsModuleConfiguration);

  readonly HttpAuthentication = HttpAuthentication;

  readonly authenticationValues = Object.values(HttpAuthentication);

  readonly methodValues = Object.values(EndpointMethod);

  @Input() value: HttpDataConnection = {} as HttpDataConnection;

  @Input() isTemplate = false;

  @Output() saveEvent = new EventEmitter<boolean>();

  @ViewChild('keyValueFormModal')
  private keyValueFormModal!: KeyValueFormComponent;

  public form!: FormGroup<HttpForm>;

  ngOnInit(): void {
    this.value.configuration = (this.value.configuration ||
      {}) as HttpConfiguration;
    this.value.endpoints = this.value.endpoints || [{}];

    // Sort endpoints by identifier
    this.value.endpoints.sort((a, b) =>
      (a.identifier || '').localeCompare(b.identifier || ''),
    );

    this.form = this.fb.group({
      name: [
        this.value.name,
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(128),
        ],
      ],
      identifier: [this.value.identifier, this.getIdentifierValidators()],
      description: [this.value.description],
      configuration: this.fb.group({
        url: [this.value.configuration.url, [Validators.required]],
        authentication: [
          this.value.configuration.authentication || HttpAuthentication.NONE,
          [Validators.required],
        ],
        username: [this.value.configuration.username],
        password: [this.value.configuration.password],
        token: [this.value.configuration.token],
        clientId: [this.value.configuration.clientId],
        clientSecret: [this.value.configuration.clientSecret],
        tokenUrl: [this.value.configuration.tokenUrl],
        scope: [this.value.configuration.scope],
        jobListEndpoint: [this.value.configuration.jobListEndpoint],
        useXSRFToken: [this.value.configuration.useXSRFToken || false],
        xsrfTokenHeader: [this.value.configuration.xsrfTokenHeader],
      }),
      endpoints: this.fb.array(
        this.value.endpoints.map((endpoint) =>
          this.createEndpointGroup(endpoint),
        ),
      ),
    });
    this.setAuthValidators(this.value.configuration.authentication);
    this.form.controls.configuration.controls.authentication.valueChanges.subscribe(
      (value: HttpAuthentication | null) => {
        this.setAuthValidators(value!);
      },
    );
    this.form.controls.configuration.controls.useXSRFToken.valueChanges.subscribe(
      (useXsrf: boolean) => {
        this.setXsrfValidators(useXsrf);
      },
    );
    this.setXsrfValidators(
      this.form.controls.configuration.controls.useXSRFToken.value,
    );
  }

  private getIdentifierValidators(): Validators[] {
    if (this.isTemplate) {
      return [
        Validators.minLength(2),
        Validators.maxLength(128),
        CustomValidators.patternValidator(/^[a-zA-Z0-9-]*$/, {
          noSpaceOrSpecial: true,
        }),
      ];
    } else {
      return [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(128),
        CustomValidators.patternValidator(/^[a-zA-Z0-9-]*$/, {
          noSpaceOrSpecial: true,
        }),
      ];
    }
  }

  private setAuthValidators(value?: HttpAuthentication): void {
    if (value === HttpAuthentication.BASIC) {
      this.form.controls.configuration.controls.username.setValidators([
        Validators.required,
      ]);
      if (!this.isTemplate) {
        this.form.controls.configuration.controls.password.setValidators([
          Validators.required,
        ]);
      } else {
        this.form.controls.configuration.controls.password.setValidators([]);
      }
      this.form.controls.configuration.controls.clientId.setValidators([]);
      this.form.controls.configuration.controls.tokenUrl.setValidators([]);
      this.form.controls.configuration.controls.token.setValidators([]);
    } else if (value === HttpAuthentication.BEARER) {
      this.form.controls.configuration.controls.username.setValidators([]);
      this.form.controls.configuration.controls.password.setValidators([]);
      if (!this.isTemplate) {
        this.form.controls.configuration.controls.token.setValidators([
          Validators.required,
        ]);
      } else {
        this.form.controls.configuration.controls.token.setValidators([]);
      }
      this.form.controls.configuration.controls.clientId.setValidators([]);
      this.form.controls.configuration.controls.tokenUrl.setValidators([]);
    } else if (value === HttpAuthentication.OAUTH_CLIENT_CREDENTIALS) {
      this.form.controls.configuration.controls.username.setValidators([]);
      this.form.controls.configuration.controls.password.setValidators([]);
      this.form.controls.configuration.controls.token.setValidators([]);
      this.form.controls.configuration.controls.tokenUrl.setValidators([
        Validators.required,
      ]);
      if (!this.isTemplate) {
        this.form.controls.configuration.controls.clientId.setValidators([
          Validators.required,
        ]);
      } else {
        this.form.controls.configuration.controls.clientId.setValidators([]);
      }
    } else {
      this.form.controls.configuration.controls.username.setValidators([]);
      this.form.controls.configuration.controls.password.setValidators([]);
      this.form.controls.configuration.controls.token.setValidators([]);
      this.form.controls.configuration.controls.clientId.setValidators([]);
      this.form.controls.configuration.controls.tokenUrl.setValidators([]);
    }
    this.form.controls.configuration.controls.username.updateValueAndValidity();
    this.form.controls.configuration.controls.password.updateValueAndValidity();
    this.form.controls.configuration.controls.token.updateValueAndValidity();
  }

  private setXsrfValidators(useXsrf?: boolean): void {
    if (useXsrf) {
      this.form.controls.configuration.controls.xsrfTokenHeader.setValidators([
        Validators.required,
      ]);
    } else {
      this.form.controls.configuration.controls.xsrfTokenHeader.setValidators(
        [],
      );
    }
    this.form.controls.configuration.controls.xsrfTokenHeader.updateValueAndValidity();
  }

  addEndpoint() {
    this.form.controls.endpoints.push(
      this.createEndpointGroup({} as DataConnectionEndpoint),
    );
  }

  deleteEndpoint(index: number) {
    this.form.controls.endpoints.removeAt(index);
    this.form.markAsDirty();
  }

  private createEndpointGroup(endpoint: DataConnectionEndpoint): FormGroup {
    return this.fb.group({
      identifier: [
        endpoint.identifier,
        [
          Validators.required,
          CustomValidators.patternValidator(/^[a-zA-Z0-9-]*$/, {
            noSpaceOrSpecial: true,
          }),
        ],
      ],
      url: [endpoint.url, [Validators.required]],
      method: [endpoint.method || EndpointMethod.GET, [Validators.required]],
      isLocal: [endpoint.isLocal ?? false],
      body: [endpoint.body || ''],
      preprocess: [endpoint.preprocess || 'return request;'],
      postprocess: [endpoint.postprocess || 'return response;'],
      header: this.fb.array(
        endpoint.header
          ? endpoint.header.map((header) => this.createKeyValueGroup(header))
          : [],
      ),
      query: this.fb.array(
        endpoint.query
          ? endpoint.query.map((query) => this.createKeyValueGroup(query))
          : [],
      ),
    });
  }

  private createKeyValueGroup(pair: { key: string; value: string }): FormGroup {
    return this.fb.group({
      key: [pair.key, [Validators.required]],
      value: [pair.value, [Validators.required]],
    });
  }

  getEndpointIdentifiers(): string[] {
    if (!this.form.value.endpoints) {
      return [];
    }
    return this.form.value.endpoints
      .map((endpoint) => endpoint.identifier)
      .filter((identifier: string | null) => identifier);
  }

  testEndpoint(endpointForm: FormGroup<EndpointForm>): void {
    try {
      const endpoint = this.value.endpoints.find(
        (ep) => ep.identifier === endpointForm.controls.identifier.value,
      )!;
      const request = {
        header: {},
        query: {},
        body: endpoint.body ? this.tryParseJson(endpoint.body) : undefined,
      };
      safeEvalWithParams(endpoint.preprocess, { request }).subscribe((r) => {
        r.body = typeof r.body !== 'string' ? JSON.stringify(r.body) : r.body;
        this.service
          .executeEndpointWithResponse(
            this.value.identifier,
            endpoint.identifier,
            r,
          )
          .subscribe({
            next: (result) => {
              safeEvalWithParams(endpoint.postprocess, {
                response: this.tryParseJson(result.responseBody),
              }).subscribe({
                next: (response) => {
                  this.openCodeModal(
                    JSON.stringify({
                      success: result.success,
                      responseBody:
                        typeof response !== 'string'
                          ? JSON.stringify(response, undefined, 2)
                          : response,
                      headers: result.headers,
                      statusCode: result.statusCode,
                    }),
                    'dataconnection.endpoint.response',
                    undefined,
                    'json',
                  );
                },
                error: (error) => {
                  this.openCodeModal(
                    error,
                    'dataconnection.endpoint.response',
                    undefined,
                    'json',
                  );
                },
              });
            },
            error: (error) => {
              this.openCodeModal(
                error,
                'dataconnection.endpoint.response',
                undefined,
                'json',
              );
            },
          });
      });
    } catch (error) {
      if (error instanceof Error) {
        this.openCodeModal(
          error,
          'dataconnection.endpoint.response',
          undefined,
          'json',
        );
      } else {
        // Handle non-Error objects if necessary
        this.openCodeModal(
          new Error(String(error)),
          'dataconnection.endpoint.response',
          undefined,
          'json',
        );
      }
    }
  }

  private tryParseJson(input: string): string | object {
    try {
      return JSON.parse(input);
    } catch (e) {
      console.error(e);
      return input;
    }
  }

  editPreprocess(endpoint: FormGroup<EndpointForm>): void {
    const modalRef = this.openCodeModal(
      endpoint.value.preprocess,
      'dataconnection.endpoint.preprocess',
      'dataconnection.endpoint.preprocess-description',
      'javascript',
      `${this.configuration.applicationUrls.documentation}/2.%20Interfaces/2http/#request-configuration`,
    );

    modalRef.closed.subscribe((code) => {
      if (typeof code === 'string') {
        endpoint.controls.preprocess.setValue(code);
        endpoint.markAsDirty();
      }
    });
  }

  editPostprocess(endpoint: FormGroup<EndpointForm>): void {
    const modalRef = this.openCodeModal(
      endpoint.value.postprocess,
      'dataconnection.endpoint.postprocess',
      'dataconnection.endpoint.postprocess-description',
      'javascript',
      `${this.configuration.applicationUrls.documentation}/2.%20Interfaces/2http/#request-configuration`,
    );

    modalRef.closed.subscribe((code) => {
      if (typeof code === 'string') {
        endpoint.controls.postprocess.setValue(code);
        endpoint.markAsDirty();
      }
    });
  }

  editBody(endpoint: FormGroup<EndpointForm>): void {
    const modalRef = this.openCodeModal(
      endpoint.value.body,
      'dataconnection.endpoint.body',
      'dataconnection.endpoint.body-description',
      'json',
    );

    modalRef.closed.subscribe((code) => {
      if (typeof code === 'string') {
        endpoint.controls.body.setValue(code);
        endpoint.markAsDirty();
      }
    });
  }

  editHeader(endpoint: FormGroup<EndpointForm>): void {
    this.keyValueFormModal.show(
      'dataconnection.endpoint.header',
      endpoint.controls.header,
      'dataconnection.endpoint.header-description',
    );
  }

  editQuery(endpoint: FormGroup<EndpointForm>): void {
    this.keyValueFormModal.show(
      'dataconnection.endpoint.query',
      endpoint.controls.query,
      'dataconnection.endpoint.query-description',
    );
  }

  save(): void {
    if (this.value.id === undefined || this.value.id === null) {
      this.value.status = DataConnectionStatus.ACTIVE;
    }

    this.saveEvent.emit(false);
  }

  openCodeModal(
    code: string | Error,
    title: string,
    description?: string,
    codeLanguage?: string,
    documentationLink?: string,
  ): AppDialogRef<CodeComponent, string> {
    const data = {
      code: code instanceof Error ? code.message : code,
      title,
      description,
      codeLanguage,
      documentationLink,
    };
    return this.modalService.open<CodeComponent, typeof data, string>(
      CodeComponent,
      {
        data,
        size: 'xl',
      },
    );
  }
}
